import { useMemo } from 'react';
import { useCachedSelectedProject } from './useCachedSelectedProject';
import { useAppSelector } from 'store/hooks';
import { EMPTY_SELECTOR } from './cachedSelectorsUtils';
import { TechnicalRuleResponse, RuleRequest } from 'store/api/apiTypes';
import { hasTechnicalRuleData } from 'shared/helpers/technicalAccessibility';
import { rulesApiSlice } from 'store/api/rulesApiSlice';

/** Hook providing LLMs rule cached data. */
export const useCachedLLmsRule = () => {
  const { selectedProject } = useCachedSelectedProject();
  const domain = selectedProject?.domain;
  const activeReport = useAppSelector(state => state.history.activeReport);
  const isGenerating = useAppSelector(
    state => state.reportGeneration.isGenerating
  );

  const queryArgs = useMemo<RuleRequest | null>(
    () => (domain ? { domain, reportId: activeReport?.id } : null),
    [domain, activeReport]
  );

  const selectRule = useMemo(
    () =>
      queryArgs
        ? rulesApiSlice.endpoints.getLLmsRule.select(queryArgs)
        : (EMPTY_SELECTOR as any),
    [queryArgs]
  );

  const {
    data,
    status,
    isLoading: loading,
    error,
  } = useAppSelector(selectRule as any) as {
    data: TechnicalRuleResponse | undefined;
    status: string;
    isLoading?: boolean;
    error?: any;
  };

  const isLoaded = !loading && !!data?.rule && !isGenerating;
  const isLoading = loading || isGenerating;

  return {
    ruleData: data,
    status,
    isLoading,
    isLoaded,
    error,
    queryArgs,
    hasRuleData: hasTechnicalRuleData(data),
    score: data?.score,
    findings: data?.findings,
  };
};
